// src/pages/AIFriendMatchingPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/socketContext';
import { useAuth } from '../contexts/authContext';
import { API_URL } from '../lib/config';

import PartnerProfileCard from '../components/matching/PartnerProfileCard';
import './MatchingPage.css'; // 기존 CSS 재사용!

const formatCooldownTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    
    // AI 친구 찾기도 쿨타임이 있으므로 동일하게 적용
    date.setMinutes(date.getMinutes() + 1);

    return date.toLocaleTimeString('ko-KR', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    });
};

function AIFriendMatchingPage() {
    const { currentUser } = useAuth();
    const [recommendation, setRecommendation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cooldownEndTime, setCooldownEndTime] = useState(null);
    const navigate = useNavigate();
    const socket = useSocket();

    const fetchRecommendation = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        setRecommendation(null);
        try {
            const idToken = await currentUser.getIdToken();
            // ★ 수정: AI 친구 랜덤 추천 API 엔드포인트
            const response = await fetch(`${API_URL}/api/ai-friends/recommend`, { 
                headers: { 'Authorization': `Bearer ${idToken}` } 
            });

            if (response.status === 429) {
                const retryAfterSeconds = parseInt(response.headers.get('Retry-After'), 10) || 120; // 2분 쿨타임 가정
                const endTime = new Date(Date.now() + retryAfterSeconds * 1000);
                const endTimeString = endTime.toISOString();
                
                // ★ 수정: AI 친구 전용 로컬스토리지 키 사용
                localStorage.setItem('aiFriendCooldownEnd', endTimeString);
                setCooldownEndTime(endTimeString);
                return;
            }
            if (!response.ok) throw new Error('AI 친구를 불러오는 데 실패했습니다.');

            localStorage.removeItem('aiFriendCooldownEnd');
            setCooldownEndTime(null);

            const data = await response.json();
            setRecommendation(data);
            
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        // ★ 수정: AI 친구 전용 키 확인
        const savedCooldownEnd = localStorage.getItem('aiFriendCooldownEnd');
        if (savedCooldownEnd && new Date(savedCooldownEnd) > new Date()) {
            setCooldownEndTime(savedCooldownEnd);
            setLoading(false);
            return; 
        }
        fetchRecommendation();
    }, [fetchRecommendation]);

    useEffect(() => {
        if (!socket) return;
        // ★ 수정: AI 채팅방 생성 완료 소켓 이벤트 (백엔드 맞춰서 수정 필요)
        const handleAIChatCreated = (data) => {
            navigate('/chat', { state: { chatRoomId: data.chatRoomId, partner: data.partner, from: '/inbox' } });
        };
        const handleErrorFeedback = ({ message }) => {
            alert(message); 
        };
        
        socket.on('ai_chat_created', handleAIChatCreated);
        socket.on('error_feedback', handleErrorFeedback);
        
        return () => {
            socket.off('ai_chat_created', handleAIChatCreated);
            socket.off('error_feedback', handleErrorFeedback);
        };
    }, [socket, navigate]);
    
    const handleStartChat = (partner) => {
        if (!socket || !currentUser) return alert('서버에 연결되지 않았습니다.');
        const initialMessage = prompt(`'${partner.nickname}' 친구에게 첫 인사를 건네보세요!`);

        if (initialMessage && initialMessage.trim() !== '') {
            // ★ 수정: AI 전용 채팅방 생성 소켓 이벤트 발생
            socket.emit('create_ai_chat', {
                senderId: localStorage.getItem('currentUserId'),
                // AI의 ID (MongoDB ObjectID)를 넘깁니다.
                aiUserId: partner.userId, 
                initialMessage: initialMessage.trim()
            });
        } else {
            alert('메시지를 입력해야 대화를 시작할 수 있습니다.');
        }
    };

    const handleRefreshRecommendation = () => {
        fetchRecommendation();
    };

    if (loading) {
        return <div className="matching-page-container"><h1>나와 맞는 AI 친구를 찾는 중...</h1></div>;
    }

    const isCooldownActive = cooldownEndTime && new Date(cooldownEndTime) > new Date();

    return (
        <div className="matching-page-container">
            <div id="stars"></div>
            <div id="stars2"></div>
            <div id="stars3"></div>

            <h1>새로운 AI 친구</h1>

            {recommendation ? (
                <p className="status-text">당신과 대화할 준비가 된 AI 친구입니다.</p>
            ) : (
                <p className="status-text">{isCooldownActive ? "잠시 후 새로운 AI 친구를 소개받을 수 있습니다." : "새로운 친구를 찾고 있습니다."}</p>
            )}

            <div className="recommendation-list">
                {recommendation && (
                    <PartnerProfileCard
                        profile={recommendation}
                        onAccept={() => handleStartChat(recommendation)}
                        onReject={() => navigate('/inbox')}
                    />
                )}
            </div>

            <div className="matching-actions">
                <button
                    className="cancel-matching-button"
                    onClick={handleRefreshRecommendation}
                    disabled={isCooldownActive || loading}
                >
                    {isCooldownActive
                        ? `${formatCooldownTime(cooldownEndTime)}에 다시 시도하세요`
                        : '다른 친구 찾기'}
                </button>
            </div>
            
            <button className="back-button" onClick={() => navigate('/inbox')}>
                쪽지함으로 돌아가기
            </button>
        </div>
    );
}

export default AIFriendMatchingPage;